import React, { useState, useEffect } from 'react';
import { Coffee, Heart, Gift, Sparkles, Cake, PartyPopper, Star, Send, Twitter, Share2, Check, AlertCircle } from 'lucide-react';
import confetti from 'canvas-confetti';

interface CardDesign {
  id: string;
  name: string;
  preview: string;
  animation: 'confetti' | 'float' | 'spin' | 'pulse' | 'explode';
  bgGradient: string;
  accentColor: string;
}

const cardDesigns: CardDesign[] = [
  {
    id: 'coffee-blast',
    name: 'Coffee Blast',
    preview: '☕💥',
    animation: 'explode',
    bgGradient: 'from-amber-400 via-orange-500 to-amber-600',
    accentColor: 'amber'
  },
  {
    id: 'cake-party',
    name: 'Cake Party',
    preview: '🎂🎉',
    animation: 'confetti',
    bgGradient: 'from-pink-400 via-rose-500 to-pink-600',
    accentColor: 'pink'
  },
  {
    id: 'stellar-wishes',
    name: 'Stellar Wishes',
    preview: '⭐✨',
    animation: 'float',
    bgGradient: 'from-indigo-400 via-blue-500 to-indigo-600',
    accentColor: 'blue'
  },
  {
    id: 'heart-warm',
    name: 'Heart Warming',
    preview: '❤️☕',
    animation: 'pulse',
    bgGradient: 'from-red-400 via-rose-500 to-red-600',
    accentColor: 'red'
  },
  {
    id: 'golden-celebration',
    name: 'Golden Hour',
    preview: '🌟🎊',
    animation: 'spin',
    bgGradient: 'from-yellow-400 via-amber-500 to-yellow-600',
    accentColor: 'yellow'
  }
];

export default function AnimatedBirthdayCards() {
  const [selectedCard, setSelectedCard] = useState<CardDesign>(cardDesigns[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recipientName, setRecipientName] = useState('Sarah');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderName, setSenderName] = useState('Alex');
  const [message, setMessage] = useState('Wishing you a brew-tiful birthday! ☕');
  const [includesCoffee, setIncludesCoffee] = useState(true);
  const [coffeeAmount, setCoffeeAmount] = useState(15);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState<any>(null);

  const playAnimation = () => {
    setIsPlaying(true);

    // Trigger confetti or other effects based on card type
    if (selectedCard.animation === 'confetti' || selectedCard.animation === 'explode') {
      const duration = 3 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

      function randomInRange(min: number, max: number) {
        return Math.random() * (max - min) + min;
      }

      const interval: any = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);

        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          colors: ['#FCD34D', '#F59E0B', '#DC2626', '#7C3AED', '#2563EB']
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          colors: ['#FCD34D', '#F59E0B', '#DC2626', '#7C3AED', '#2563EB']
        });
      }, 250);
    }

    setTimeout(() => setIsPlaying(false), 3000);
  };

  const generateTweetText = () => {
    return `🎉 Happy Birthday ${recipientName}! 🎂 ${includesCoffee ? `\n\n☕ Enjoy your birthday coffee! Sent with love via @birthdaycoffee` : `\n\nSent with love via @birthdaycoffee`}`;
  };

  const shareOnTwitter = () => {
    const tweetText = encodeURIComponent(generateTweetText());
    window.open(`https://twitter.com/intent/tweet?text=${tweetText}`, '_blank');
  };

  const handleSendCard = async () => {
    // Validate inputs
    if (!recipientName || !recipientEmail || !senderName || !message) {
      alert('Please fill in all required fields');
      return;
    }

    setIsSending(true);
    setSendResult(null);

    try {
      const response = await fetch('https://birthday-coffee-api.b-9f2.workers.dev/api/cards/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipientName,
          recipientEmail,
          senderName,
          message,
          cardDesign: selectedCard.id,
          includesCoffee,
          coffeeAmount,
          tweetText: generateTweetText()
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSendResult({
          success: true,
          ...data
        });

        // Auto-share on Twitter after successful send
        if (includesCoffee) {
          setTimeout(() => {
            shareOnTwitter();
          }, 1500);
        }
      } else {
        setSendResult({
          success: false,
          error: data.error || 'Failed to send card'
        });
      }
    } catch (error) {
      setSendResult({
        success: false,
        error: error instanceof Error ? error.message : 'Network error'
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-amber-900 mb-2">Animated Birthday Cards</h1>
          <p className="text-amber-700">Send a card that's worth sharing on social media</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Card Preview */}
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-amber-900">Card Preview</h2>
              <button
                onClick={playAnimation}
                disabled={isPlaying}
                className="bg-amber-600 text-white px-6 py-2 rounded-full font-semibold hover:bg-amber-700 transition-colors disabled:bg-gray-400"
              >
                {isPlaying ? 'Playing...' : 'Play Animation'}
              </button>
            </div>

            {/* Animated Card Display */}
            <div className={`relative bg-gradient-to-br ${selectedCard.bgGradient} rounded-2xl p-8 h-96 flex flex-col items-center justify-center text-white overflow-hidden`}>
              {/* Background animations */}
              <div className={`absolute inset-0 ${isPlaying ? 'animate-pulse' : ''}`}>
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className={`absolute ${isPlaying ? 'animate-float' : ''}`}
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      animationDelay: `${i * 0.5}s`
                    }}
                  >
                    {i % 2 === 0 ? (
                      <Sparkles className="w-8 h-8 text-white/30" />
                    ) : (
                      <Star className="w-6 h-6 text-white/20" />
                    )}
                  </div>
                ))}
              </div>

              {/* Main Content */}
              <div className={`relative z-10 text-center ${isPlaying ? 'animate-bounce' : ''}`}>
                <div className="text-6xl mb-4">
                  {selectedCard.preview}
                </div>
                <h3 className="text-3xl font-bold mb-2">
                  Happy Birthday, {recipientName}!
                </h3>
                <p className="text-xl mb-4 opacity-90">
                  {message}
                </p>
                {includesCoffee && (
                  <div className="bg-white/20 backdrop-blur rounded-xl px-4 py-2 inline-flex items-center gap-2">
                    <Coffee className="w-5 h-5" />
                    <span className="font-semibold">${coffeeAmount} Coffee Gift Included!</span>
                  </div>
                )}
                <p className="text-sm mt-4 opacity-75">
                  From: {senderName}
                </p>
              </div>

              {/* Corner decorations */}
              <div className={`absolute top-4 left-4 ${isPlaying ? 'animate-spin' : ''}`}>
                <Cake className="w-12 h-12 text-white/40" />
              </div>
              <div className={`absolute bottom-4 right-4 ${isPlaying ? 'animate-spin-reverse' : ''}`}>
                <PartyPopper className="w-12 h-12 text-white/40" />
              </div>
            </div>

            {/* Share Options */}
            <div className="mt-6 flex gap-4">
              <button
                onClick={shareOnTwitter}
                className="flex-1 bg-black text-white px-6 py-3 rounded-full font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
              >
                <Twitter className="w-5 h-5" />
                Share on Twitter
              </button>
              <button
                onClick={() => setShowShareOptions(!showShareOptions)}
                className="bg-white border-2 border-amber-300 text-amber-700 px-6 py-3 rounded-full font-semibold hover:bg-amber-50 transition-colors flex items-center justify-center gap-2"
              >
                <Share2 className="w-5 h-5" />
                More Options
              </button>
            </div>

            {showShareOptions && (
              <div className="mt-4 p-4 bg-amber-50 rounded-xl">
                <p className="text-sm text-amber-700 mb-2">Share link:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={`birthday.coffee/card/${selectedCard.id}/${recipientName}`}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border border-amber-300 rounded-lg text-sm"
                  />
                  <button className="bg-amber-600 text-white px-4 py-2 rounded-lg text-sm">
                    Copy
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Customization Panel */}
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <h2 className="text-xl font-bold text-amber-900 mb-6">Customize Your Card</h2>

            {/* Card Designs */}
            <div className="mb-6">
              <label className="block text-amber-700 font-semibold mb-3">Choose a Design</label>
              <div className="grid grid-cols-5 gap-3">
                {cardDesigns.map((design) => (
                  <button
                    key={design.id}
                    onClick={() => setSelectedCard(design)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      selectedCard.id === design.id
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 hover:border-amber-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">{design.preview}</div>
                    <p className="text-xs text-gray-600">{design.name}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Recipient Name */}
            <div className="mb-6">
              <label className="block text-amber-700 font-semibold mb-2">Recipient's Name</label>
              <input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:border-amber-400 focus:outline-none"
                placeholder="Enter their name"
              />
            </div>

            {/* Recipient Email */}
            <div className="mb-6">
              <label className="block text-amber-700 font-semibold mb-2">Recipient's Email</label>
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:border-amber-400 focus:outline-none"
                placeholder="their.email@example.com"
                required
              />
            </div>

            {/* Message */}
            <div className="mb-6">
              <label className="block text-amber-700 font-semibold mb-2">Birthday Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:border-amber-400 focus:outline-none h-24"
                placeholder="Write your birthday message..."
              />
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => setMessage('Hope your day is as amazing as you are! 🎉')}
                  className="text-xs bg-amber-100 text-amber-700 px-3 py-1 rounded-full"
                >
                  Classic
                </button>
                <button
                  onClick={() => setMessage('Another year older, another year awesome! 🎂')}
                  className="text-xs bg-amber-100 text-amber-700 px-3 py-1 rounded-full"
                >
                  Fun
                </button>
                <button
                  onClick={() => setMessage('Wishing you a brew-tiful birthday! ☕')}
                  className="text-xs bg-amber-100 text-amber-700 px-3 py-1 rounded-full"
                >
                  Coffee Pun
                </button>
              </div>
            </div>

            {/* Sender Name */}
            <div className="mb-6">
              <label className="block text-amber-700 font-semibold mb-2">Your Name</label>
              <input
                type="text"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:border-amber-400 focus:outline-none"
                placeholder="Your name"
              />
            </div>

            {/* Coffee Gift Option */}
            <div className="mb-6 p-4 bg-orange-50 rounded-xl border-2 border-orange-200">
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includesCoffee}
                    onChange={(e) => setIncludesCoffee(e.target.checked)}
                    className="w-5 h-5 text-amber-600"
                  />
                  <span className="font-semibold text-amber-700">Include Coffee Gift</span>
                </label>
                <Coffee className="w-6 h-6 text-amber-600" />
              </div>
              {includesCoffee && (
                <div className="flex gap-2">
                  {[5, 10, 15, 25].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setCoffeeAmount(amount)}
                      className={`flex-1 py-2 rounded-lg font-semibold transition-colors ${
                        coffeeAmount === amount
                          ? 'bg-amber-600 text-white'
                          : 'bg-white text-amber-700 border border-amber-300'
                      }`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Send Button */}
            <button
              onClick={handleSendCard}
              disabled={isSending}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSending ? (
                <>
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending Card...
                </>
              ) : (
                <>
                  <Send className="w-6 h-6" />
                  Send Birthday Card
                  {includesCoffee && <span className="text-amber-100">(+${coffeeAmount} Coffee)</span>}
                </>
              )}
            </button>

            {/* Send Result */}
            {sendResult && (
              <div className={`mt-6 p-4 rounded-xl ${sendResult.success ? 'bg-green-100 border-2 border-green-300' : 'bg-red-100 border-2 border-red-300'}`}>
                {sendResult.success ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="w-6 h-6 text-green-600" />
                      <p className="font-bold text-green-800">Card Sent Successfully!</p>
                    </div>
                    <p className="text-green-700 text-sm mb-2">Gift Code: <span className="font-mono font-bold">{sendResult.giftCode}</span></p>
                    {sendResult.revenue > 0 && (
                      <p className="text-green-700 text-sm">Revenue earned: <span className="font-bold">${sendResult.revenue.toFixed(2)}</span></p>
                    )}
                    <div className="mt-3 flex gap-2">
                      <a
                        href={sendResult.cardUrl}
                        target="_blank"
                        className="text-sm bg-green-600 text-white px-4 py-2 rounded-full"
                      >
                        View Card
                      </a>
                      <button
                        onClick={() => {
                          setSendResult(null);
                          setRecipientEmail('');
                          setMessage('Wishing you a brew-tiful birthday! ☕');
                        }}
                        className="text-sm bg-amber-600 text-white px-4 py-2 rounded-full"
                      >
                        Send Another
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                    <p className="text-red-700">{sendResult.error}</p>
                  </div>
                )}
              </div>
            )}

            {/* Social Proof */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Cards are automatically shared to Twitter • Seen by avg. 127 people
              </p>
              <div className="flex justify-center gap-4 mt-2">
                <span className="text-xs text-amber-600">🔥 2,847 cards sent today</span>
                <span className="text-xs text-rose-600">❤️ 98% include coffee</span>
              </div>
            </div>
          </div>
        </div>

        {/* Animation Styles */}
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(10deg); }
          }
          @keyframes spin-reverse {
            from { transform: rotate(0deg); }
            to { transform: rotate(-360deg); }
          }
          .animate-float {
            animation: float 3s ease-in-out infinite;
          }
          .animate-spin-reverse {
            animation: spin-reverse 3s linear infinite;
          }
        `}</style>
      </div>
    </div>
  );
}